import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  BusinessDayCloseController_closeDay_v1,
  type BusinessDayCloseController_closeDay_v1Body,
  type DayCloseResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { formatPaise, paiseFromRupees } from '@/lib/money';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { formatBusinessDate } from '../tickets/business-date';
import { closeDaySchema, type CloseDayValues } from './day-close-form';

/**
 * Shutting a trading day:
 * `POST /v1/outlets/{outletId}/business-days/{businessDate}/close`.
 *
 * ## There is nothing here to be talked out of
 *
 * **No variance threshold, no confirm-if-large step, and no second press.** The
 * API is explicit that closing never fails for a business reason (CL-601,
 * FR-619a): there is no amount the route refuses, a count of ₹0 closes a day
 * that took nothing, and a count far above the takings closes too. A screen that
 * added "are you sure, that is ₹4,000 short?" would be inventing a refusal that
 * does not exist server-side — and worse, teaching the person counting that a
 * number the system dislikes is a number to change.
 *
 * The variance is **derived** from what was counted and what the ledger holds
 * (FR-616). The operator submits what they counted and never a difference, which
 * is the same rule the stocktake screen keeps for quantities.
 *
 * ## What the expected figure is doing on the other side of this dialog
 *
 * Deliberately not repeated beside the field. The reconciliation behind the
 * dialog states it — it is a published figure and hiding it would be a different
 * kind of dishonesty — but putting it *next to the input* turns counting into
 * copying, and the API stores what is submitted exactly and never adjusts it
 * towards what it expected (FR-615). The variance is shown afterwards, where it
 * is a fact about the day rather than a hint about what to type.
 *
 * ## One key while the figure stands, a fresh one when it moves
 *
 * `lib/write-attempt.ts` states the rule and the two ways of getting it wrong. A
 * key held for the dialog's life earns `409 SYNC_IDEMPOTENCY_KEY_REUSED` the
 * first time somebody is refused, corrects the figure and presses again — a
 * refusal no retry can clear. A fresh key per press replays nothing, so a
 * dropped answer leaves somebody unable to tell whether the day is closed.
 *
 * `CloseBusinessDayDto` carries no client-minted `id`, so only the attempt's
 * `key` is read here; the `id` it mints alongside is unused and costs a UUID.
 * The alternative is a fourth idempotency pattern on this screen for the sake
 * of one field, which is how the three that exist became three.
 */
export function CloseDayDialog({
  open,
  onOpenChange,
  outletId,
  outletName,
  businessDate,
  onClosed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outletId: string;
  outletName: string;
  businessDate: string;
  onClosed: (closed: DayCloseResource) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>Close {formatBusinessDate(businessDate)}</DialogTitle>
          <DialogDescription>
            {outletName}. Enter what was physically counted in the drawer. Whatever the figure, the
            day closes — the difference between it and the books is worked out and recorded, never
            asked for, and it never stops a close.
          </DialogDescription>
        </DialogHeader>

        {/* A child, so closing the dialog unmounts it — Base UI's portal does not
            keep its content mounted. That is what makes the next open start from
            an empty field rather than from a half-typed count, and what keeps
            the attempt below belonging to one cash-up. */}
        <CloseDayForm outletId={outletId} businessDate={businessDate} onClosed={onClosed} />
      </FormDialogContent>
    </Dialog>
  );
}

function CloseDayForm({
  outletId,
  businessDate,
  onClosed,
}: {
  outletId: string;
  businessDate: string;
  onClosed: (closed: DayCloseResource) => void;
}) {
  const client = useApiClient();

  const form = useForm<CloseDayValues>({
    resolver: zodResolver(closeDaySchema),
    mode: 'onTouched',
    // Empty and not zero. A prefilled `0` is a figure nobody counted, and on
    // this field it is a *valid* one — so it would close a day on an untouched
    // form. The schema's required message says zero is an answer that has to be
    // typed.
    defaultValues: { countedCash: '' },
  });

  const [formError, setFormError] = useState<string | null>(null);

  /** The attempt this dialog is currently under. Nothing renders from it. */
  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting;

  const handleSubmit = async (values: CloseDayValues) => {
    // Belt and braces with the disabled button. A second press carries the same
    // key and body so the API replays rather than closing twice, but it would
    // still put this form into a second in-flight state for nothing.
    if (pending) return;

    setFormError(null);

    // Integer paise, moved as digits by `lib/money.ts` and never by multiplying
    // a decimal — ₹12,400 is 1240000, never 12400.00 and never a float. This is
    // the field where a float would be most tempting and most wrong.
    const countedCashPaise = paiseFromRupees(values.countedCash);

    const sending = attemptFor(
      attempt.current,
      JSON.stringify([outletId, businessDate, countedCashPaise]),
    );

    attempt.current = sending;

    try {
      // The body's shape comes from the generated client. Nothing about this
      // route's path or payload is written down in this file, and there is
      // deliberately no variance, no actor and no instant to carry — the server
      // derives all three.
      const body: BusinessDayCloseController_closeDay_v1Body = { countedCashPaise };

      onClosed(
        await client.request(BusinessDayCloseController_closeDay_v1, {
          path: { outletId, businessDate },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error: unknown) {
      if (error instanceof ApiError && error.code === 'DAY_CLOSE_COUNT_INVALID') {
        // The schema refuses everything this covers before the wire, so a body
        // that got here is a bound moved upstream or a tab left open across a
        // deploy. Put on the field it is about; the sentence is
        // `error-messages.ts`'s and not this screen's.
        form.setError('countedCash', { message: messageForFailure(error) });
        form.setFocus('countedCash');

        return;
      }

      // Everything else — the day already closed under somebody else, an outlet
      // out of reach, the network — is about the day and not about the figure.
      setFormError(messageForFailure(error));

      // Nothing is cleared. The count stands, and pressing again under the same
      // key replays rather than closing a second time.
    }
  };

  const typed = form.watch('countedCash').trim();

  // What the field currently says, in the shape it will be recorded as. Shown
  // because the field takes rupees and the record is paise, and somebody typing
  // `12400.5` should see ₹12,400.50 before they commit a day to it.
  const previewPaise = /^\d+(\.\d{1,2})?$/.test(typed) ? paiseFromRupees(typed) : null;

  return (
    // One column, per docs/ui-conventions.md. `noValidate` because the browser's
    // own bubbles are not this app's error language and cannot be worded.
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <FormField
          control={form.control}
          name="countedCash"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Counted in the drawer
              </FormLabel>
              <div className="relative">
                <span
                  // Decorative: the label already says what this is, and a
                  // reader hearing "rupees" between the label and the value
                  // gains nothing.
                  aria-hidden
                  className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-muted-foreground"
                >
                  ₹
                </span>
                <FormControl
                  render={
                    <Input
                      // `inputMode` and not `type="number"`, which hands the
                      // browser its own opinion — a spinner, a wheel that
                      // changes the figure under a pointer, and a value that
                      // reads back empty as `NaN`.
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="12400.00"
                      className={`${fieldSurface} h-11 w-full pl-8 font-mono numeric`}
                      required
                      {...field}
                    />
                  }
                />
              </div>
              <FormDescription>
                Rupees and paise, with no ₹ and no commas. Zero is a real answer and closes the day
                — a day whose takings were all UPI is a day with an empty drawer.
                {previewPaise !== null && (
                  <>
                    {' '}
                    Recorded as{' '}
                    <span className="font-mono numeric">{formatPaise(previewPaise)}</span>.
                  </>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <p className="text-sm text-muted-foreground">
          The difference against the books is worked out from this figure and kept with the day. It
          is a fact about the day rather than something anybody has to clear, and an owner can
          reopen the day afterwards if the count turns out to be wrong.
        </p>

        {/* Above the submit and it stays there — a toast vanishes, and somebody
            who looked away has no way to get it back. */}
        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending}>
            {pending ? 'Closing…' : 'Close the day'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
